import React, { useState, useEffect } from 'react';
import { Box, Card, Table, Stack, Button, Checkbox, TableRow, TableBody, TableCell, Container, Typography, TableContainer, TablePagination, TextField, InputAdornment, MenuItem, Select, FormControl, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import chevronDownFill from '@iconify/icons-eva/chevron-down-fill';
import alertCircleFill from '@iconify/icons-eva/alert-circle-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import api from '../components/api/api';
import Page from '../components/Page';


export const Payment = () => {
  const [selected, setSelected] = useState([]);
  const [partner, setPartner] = useState('');
  const [openHoldModal, setOpenHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState([]);
  const [holdComment, setHoldComment] = useState('');
  const [filterName, setFilterName] = useState('');
  const [partnersList, setPartnersList] = useState([]);
  const [farmersList, setFarmersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFarmers, setTotalFarmers] = useState(0);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [partnersLoading, setPartnersLoading] = useState(false);

  useEffect(() => {
    getPartners();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchFarmers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [partner, filterName, page, rowsPerPage]);

  const getPartners = async () => {
    try {
      setPartnersLoading(true);
      const { data } = await api.post('/getProjectsAndPartners?projectType=CROPLAND&listName=PARTNER');
      console.log('Partners API Response:', data);
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.data)) {
        list = data.data;
      }


      setPartnersList(list);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setPartnersLoading(false);
    }
  };

  const fetchFarmers = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: page + 1,
        pageSize: rowsPerPage
      };


      if (filterName && filterName.trim().length >= 3) {
        params.search = filterName.trim();
      }


      if (partner && partner !== 'ALL') {
        params.partnerId = partner;
      }
      console.log('Fetching farmers with params:', params);
      const { data } = await api.get('/farmers', { params });
      console.log('Farmers API Response:', data);

      if (data && data.content) {
        setFarmersList(data.content);
        setTotalFarmers(data.totalElements ?? data.total ?? data.totalCount ?? 0);
      } else if (data && data.data && data.data.content) {
        setFarmersList(data.data.content);
        setTotalFarmers(data.data.totalElements ?? data.data.total ?? data.data.totalCount ?? 0);
      } else if (data && data.farmers) {
        setFarmersList(data.farmers);
        setTotalFarmers(data.totalElements ?? data.total ?? data.totalCount ?? data.farmers.length);
      } else if (data && data.data && data.data.farmers) {
        setFarmersList(data.data.farmers);
        setTotalFarmers(data.data.totalElements ?? data.data.total ?? data.data.totalCount ?? data.data.farmers.length);
      } else if (data && Array.isArray(data.data)) {
        setFarmersList(data.data);
        setTotalFarmers(data.totalElements ?? data.total ?? data.totalCount ?? data.data.length);
      } else if (data && data.data && Array.isArray(data.data.data)) {
        setFarmersList(data.data.data);
        setTotalFarmers(data.data.totalElements ?? data.data.total ?? data.data.totalCount ?? data.data.data.length);
      } else if (Array.isArray(data)) {
        setFarmersList(data);
        setTotalFarmers(data.length);
      } else {
        setFarmersList([]);
        setTotalFarmers(0);
      }
    } catch (error) {
      console.error('Error fetching farmers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event, id) => {
    event.stopPropagation();
    if (selected.includes(id)) {
      setSelected([]);
    } else {
      setSelected([id]);
    }
  };

  const handlePartnerChange = (event) => {
    setPartner(event.target.value);
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReasonChange = (reason, isChecked) => {
    if (isChecked) {
      setHoldReason(prev => [...prev, reason]);
    } else {
      setHoldReason(prev => prev.filter(r => r !== reason));
    }
  };

  const handleConfirmHold = async () => {
    try {
      setLoading(true);
      const combinedComment = [...holdReason, holdComment].filter(Boolean).join(' - ');

      const promises = selected.map(farmerId =>
        api.patch(`/farmer/${farmerId}/payment`, {
          status: 'ON_HOLD',
          reason: combinedComment
        })
      );

      await Promise.all(promises);

      setFarmersList(prevList => prevList.map(farmer => {
        const farmerId = farmer.farmerId || farmer.id;
        if (selected.includes(farmerId)) {
          return {
            ...farmer,
            status: 'ON HOLD',
            paymentStatus: 'ON HOLD',
            comments: combinedComment,
            reason: combinedComment
          };
        }
        return farmer;
      }));
      setOpenConfirmDialog(false);
      setOpenHoldModal(false);
      setHoldReason([]);
      setHoldComment('');
      setSelected([]);
      fetchFarmers();
    } catch (error) {
      console.error('Error putting farmers on hold:', error);
      alert('Failed to update payment status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyles = (status) => {
    const s = status ? status.toUpperCase() : 'ELIGIBLE';
    if (s === 'CONFIRMED' || s === 'ACTIVE' || s === 'VERIFIED') {
      return { label: 'ACTIVE', bgcolor: '#D0FAE5', color: '#007A55' };
    }
    if (s === 'ELIGIBLE') {
      return { label: 'ELIGIBLE', bgcolor: '#D0FAE5', color: '#007A55' };
    }
    if (s === 'REWORK' || s === 'UNCONFIRMED' || s === 'ON HOLD' || s === 'ON_HOLD') {
      return { label: 'ON HOLD', bgcolor: '#FFF4E5', color: '#B76E00' };
    }
    return { label: status || 'UNKNOWN', bgcolor: '#FDE2E2', color: '#EF4444' };
  };

  return (
    <Page title="Payment Status">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Payment Status
          </Typography>
        </Stack>

        <Card sx={{ p: 3, borderRadius: '20px', border: '1px solid rgba(29, 29, 31, 0.1)', boxShadow: 'none' }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                value={filterName}
                onChange={handleFilterByName}
                placeholder="Search farmers (min 3 chars)..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon={searchFill} width={20} height={20} color="#1D1D1F" style={{ opacity: 0.6 }} />
                    </InputAdornment>
                  ),
                  endAdornment: filterName && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setFilterName('');
                          setPage(0);
                        }}
                      >
                        <Icon icon={closeFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '90px',
                    bgcolor: '#F8F8F8',
                    '& fieldset': { border: 'none' },
                    '& input': { py: 2, fontSize: '14px' }
                  }
                }}
              />
              <Autocomplete
                sx={{ width: { xs: '100%', sm: 550 } }}
                options={partnersList}
                loading={partnersLoading}
                getOptionLabel={(option) => (option.id === 'ALL' ? 'All Partners' : `${option.id} - ${option.name}`)}
                value={partnersList.find(p => p.id === partner) || null}
                onChange={(event, newValue) => {
                  setPartner(newValue ? newValue.id : '');
                  setPage(0);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select partner"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '90px',
                        bgcolor: '#F8F8F8',
                        '& fieldset': { border: 'none' },
                        '& input': { py: 0.5, fontSize: '14px' }
                      }
                    }}
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper sx={{
                    mt: 1,
                    borderRadius: '12px',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    minWidth: 400
                  }}>
                    {children}
                  </Paper>
                )}
              />
            </Stack>


            {selected.length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#EFF6FF',
                  border: '1px solid #BEDBFF',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="subtitle2" sx={{ color: '#1C398E' }}>
                    {selected.length} farmers selected
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Icon icon={alertCircleFill} />}
                    onClick={() => setOpenHoldModal(true)}
                    sx={{
                      color: '#CA3500',
                      borderColor: '#FFB86A',
                      bgcolor: 'white',
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': { borderColor: '#FFB86A', bgcolor: '#FFF8F0' }
                    }}
                  >
                    Put Selected on Hold
                  </Button>
                </Stack>
                <Button
                  onClick={() => setSelected([])}
                  sx={{ color: '#0A0A0A', textTransform: 'none', fontWeight: 500 }}
                >
                  Clear Selection
                </Button>
              </Paper>
            )}


            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableBody>

                  <TableRow sx={{ bgcolor: '#F2FEF6', borderRadius: '8px' }}>
                    <TableCell padding="checkbox" sx={{ borderBottom: 'none', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' }} />
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '200px' }}>Farmer name</TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '150px' }}>Phone number</TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '150px' }}>Farmer ID</TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '150px' }}>Status</TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '150px' }}>Partner ID</TableCell>
                    <TableCell sx={{ borderBottom: 'none', fontSize: '12px', color: 'black', width: '200px', borderTopRightRadius: '8px', borderBottomRightRadius: '8px' }}>Comments</TableCell>
                  </TableRow>


                  {loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading && farmersList.length > 0 && (
                    farmersList.map((row, index) => {
                      const rowId = row.farmerId || row.id || index;
                      const isItemSelected = selected.includes(rowId);
                      const statusInfo = getStatusStyles(row.paymentStatus);
                      const isOnHold = statusInfo.label === 'ON HOLD';

                      return (
                        <TableRow
                          key={rowId}
                          hover={!isOnHold}
                          onClick={(event) => !isOnHold && handleClick(event, rowId)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          selected={isItemSelected}
                          sx={{
                            borderBottom: '1px solid rgba(29, 29, 31, 0.1)',
                            cursor: isOnHold ? 'default' : 'pointer'
                          }}
                        >
                          <TableCell padding="checkbox">
                            {!isOnHold && (
                              <Checkbox
                                checked={isItemSelected}
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{row.fullname || row.name || row.farmerName || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{row.phone || row.phoneNumber || row.mobile || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{row.farmerId || row.id || '-'}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                bgcolor: statusInfo.bgcolor,
                                color: statusInfo.color
                              }}
                            >
                              {statusInfo.label}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{row.partnerId || row.partner || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{row.comments || row.comment || row.reason || row.paymentReason || row.holdReason || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}

                  {!loading && farmersList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 10, color: 'text.secondary' }}>
                        No farmers found matching your criteria
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalFarmers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Stack>
        </Card>
      </Container>

      <Dialog open={openHoldModal} onClose={() => setOpenHoldModal(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1, maxWidth: '440px', width: '100%' } }}>
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#1d1d1f' }}>Put Payment on Hold</Typography>
            <Typography sx={{ fontSize: '12px', color: '#1d1d1f', opacity: 0.6, mt: 0.5 }}>Please provide a reason for putting this payment on hold</Typography>
          </Box>
          <IconButton onClick={() => setOpenHoldModal(false)} sx={{ alignSelf: 'flex-start' }}>
            <Icon icon={closeFill} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1d1d1f', mb: 1, mt: 1 }}>Select reason(s):</Typography>
          <FormGroup sx={{ gap: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked={holdReason.includes('Death of a farmer')} onChange={(e) => handleReasonChange('Death of a farmer', e.target.checked)} sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
              label={<Typography sx={{ fontSize: '12px', color: '#1d1d1f' }}>Death of a farmer</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox checked={holdReason.includes('Sustainable practices not completed')} onChange={(e) => handleReasonChange('Sustainable practices not completed', e.target.checked)} sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
              label={<Typography sx={{ fontSize: '12px', color: '#1d1d1f' }}>Sustainable practices not completed</Typography>}
              sx={{ m: 0 }}
            />
          </FormGroup>

          <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#1d1d1f', mt: 2, mb: 0.5 }}>Additional notes (optional):</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Write additional details here..."
            value={holdComment}
            onChange={(e) => setHoldComment(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{
              bgcolor: '#fafafa',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '12px',
                '& fieldset': { borderColor: 'rgba(29,29,31,0.2)' }
              }
            }}
          />
          <Typography sx={{ fontSize: '12px', color: '#99a1af', mt: 0.5 }}>{holdComment.length}/200 characters</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setOpenHoldModal(false)} sx={{ color: '#007A55', fontSize: '14px', fontWeight: 500, textTransform: 'none', '&:hover': { bgcolor: '#F0FDF4' } }}>
            Cancel
          </Button>
          <Button
            onClick={() => setOpenConfirmDialog(true)}
            sx={{
              bgcolor: '#007A55',
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              py: 1,
              '&:hover': { bgcolor: '#005F41' }
            }}
          >
            Confirm On Hold
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Are you sure?</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '14px' }}>
            Are you sure you want to put the selected payment on hold?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            sx={{ color: '#007A55', fontWeight: 600, textTransform: 'none' }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmHold}
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: '#007A55',
              color: 'white',
              px: 4,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { bgcolor: '#005F41' }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Yes, Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};


